/* eslint-disable jsx-a11y/label-has-associated-control */
import Select from 'react-select';
import PropType from 'prop-types';
import { useField } from 'formik';
import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import comuni from '../../files/comuni.json';
import province from '../../files/province.json';
import regioni from '../../files/regioni.json';
import stati from '../../files/stati.json';
import npaCodes from '../../files/npa.json';

const CustomAddressSelect = (props) => {
  const { t } = useTranslation();
  const [field, meta, helpers] = useField(props);
  const {
    defaultMunicipality,
    defaultProvince,
    defaultRegion,
    defaultCountry,
    defaultNpa
  } = props;
  const { touched, error } = meta;
  const { setValue } = helpers;
  const [selectedCountry, setSelectedCountry] = useState(defaultCountry);
  const [selectedRegion, setSelectedRegion] = useState(defaultRegion);
  const [selectedProvince, setSelectedProvince] = useState(defaultProvince);
  const [selectedMunicipality, setSelectedMunicipality] =
    useState(defaultMunicipality);
  const [selectedNpa, setSelectedNpa] = useState(defaultNpa);

  function parseCountries() {
    return stati.map((data) => ({ label: data.nome, value: data.nome }));
  }
  const [countries] = useState(parseCountries);

  function parseRegions() {
    return regioni.map((data) => ({ label: data.nome, value: data.nome }));
  }
  const [regions, setRegions] = useState(parseRegions);

  function parseProvinces() {
    return province
      .filter((data) => data.regione === selectedRegion)
      .map((data) => ({ label: data.nome, value: data.nome }));
  }
  const [provinces, setProvinces] = useState(parseProvinces);

  function parseMunicipalities() {
    return comuni
      .filter((data) => data.Provincia === selectedProvince)
      .map((data) => ({ label: data.Comune, value: data.Comune }));
  }
  const [municipalities, setMunicipalities] = useState(parseMunicipalities);

  function parseNpa() {
    const keys = Object.keys(npaCodes);
    return keys.map((data) => ({ label: data, value: data }));
  }
  const [npa, setNpa] = useState(parseNpa);

  const handleChangeCountry = (value) => {
    setSelectedCountry(value.value);
  };

  const handleChangeRegion = (value) => {
    setSelectedRegion(value.value);
  };

  const handleChangeProvince = (value) => {
    setSelectedProvince(value.value);
  };

  const handleChangeMunicipality = (value) => {
    setSelectedMunicipality(value.value);
  };

  const handleChangeNpa = (value) => {
    setSelectedNpa(value.value);
  };

  function updateValue() {
    const add = {
      country: selectedCountry,
      region: selectedRegion,
      province: selectedProvince,
      municipality: selectedMunicipality,
      npa: selectedNpa
    };
    setValue(add);
  }

  function useEffectCountry() {
    const didMountRef = useRef(false);
    useEffect(() => {
      if (didMountRef.current) {
        if (selectedCountry === 'Italia') {
          const r = parseRegions();
          setRegions(r);
          setSelectedRegion(r[0].value);
        } else if (selectedCountry === 'Svizzera') {
          const n = parseNpa();
          setNpa(n);
          setSelectedNpa(n[0].value);
        }
      }
      didMountRef.current = true;
    }, [selectedCountry]);
  }
  useEffectCountry();

  function useEffectRegion() {
    const didMountRef = useRef(false);
    useEffect(() => {
      if (didMountRef.current) {
        const p = parseProvinces();
        setProvinces(p);
        setSelectedProvince(p[0].value);
      }
      didMountRef.current = true;
    }, [selectedRegion]);
  }
  useEffectRegion();

  function useEffectProvince() {
    const didMountRef = useRef(false);
    useEffect(() => {
      if (didMountRef.current) {
        const m = parseMunicipalities();
        setMunicipalities(m);
        setSelectedMunicipality(m[0].value);
      }
      didMountRef.current = true;
    }, [selectedProvince]);
  }
  useEffectProvince();

  function useEffectMunicipality() {
    const didMountRef = useRef(false);
    useEffect(() => {
      if (didMountRef.current) {
        setMunicipalities(parseMunicipalities());
        updateValue();
      }
      didMountRef.current = true;
    }, [selectedMunicipality]);
  }
  useEffectMunicipality();

  function useEffectNpa() {
    const didMountRef = useRef(false);
    useEffect(() => {
      if (didMountRef.current) {
        setNpa(parseNpa());
        updateValue();
      }
      didMountRef.current = true;
    }, [selectedNpa]);
  }
  useEffectNpa();

  return (
    <div className="input-group">
      <label className="label-input">{t('userFieldCountry')}</label>
      {touched && error ? <div className="error">{meta.error}</div> : null}
      <Select
        id={field.name}
        name={field.name}
        options={countries}
        value={{ label: selectedCountry, value: selectedCountry }}
        onChange={handleChangeCountry}
      />
      {selectedCountry === 'Italia' && (
        <>
          <label className="label-input">{t('userFieldRegion')}</label>
          <Select
            id={field.name}
            name={field.name}
            options={regions}
            value={{ label: selectedRegion, value: selectedRegion }}
            onChange={handleChangeRegion}
          />
          <label className="label-input">{t('userFieldProvince')}</label>
          <Select
            id={field.name}
            name={field.name}
            options={provinces}
            value={{ label: selectedProvince, value: selectedProvince }}
            onChange={handleChangeProvince}
          />
          <label className="label-input">{t('userFieldMunicipality')}</label>
          <Select
            id={field.name}
            name={field.name}
            options={municipalities}
            value={{ label: selectedMunicipality, value: selectedMunicipality }}
            onChange={handleChangeMunicipality}
          />
        </>
      )}
      {selectedCountry === 'Svizzera' && (
        <>
          <label className="label-input">NPA</label>
          <Select
            id={field.name}
            name={field.name}
            options={npa}
            value={{ label: selectedNpa, value: selectedNpa }}
            onChange={handleChangeNpa}
          />
        </>
      )}
    </div>
  );
};

CustomAddressSelect.defaultProps = {
  defaultMunicipality: '',
  defaultProvince: '',
  defaultRegion: '',
  defaultCountry: '',
  defaultNpa: ''
};

CustomAddressSelect.propTypes = {
  defaultMunicipality: PropType.string,
  defaultProvince: PropType.string,
  defaultRegion: PropType.string,
  defaultCountry: PropType.string,
  defaultNpa: PropType.string
};

export default CustomAddressSelect;
